import { PropsWithDataTest } from "../../types";
import {
  Button,
  Input,
  Box,
  FlexColBox,
  Form,
  PasswordStrengthIndicator,
} from "../../common/components";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ReactComponent as EditIcon } from "../../assets/icons/edit_icon.svg";
import { useForm } from "../../common/hooks/useForm";
import { object, string } from "yup";
import { testPassword } from "../../utils/FormValidationsUtils";
import AuthService from "../../service/authService";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

interface Props extends PropsWithDataTest {
  onConfirm?: (value: string) => void;
}

const COMMON_PASSWORD_ERROR = "Password is too common";

export const UpdatePasswordInput = ({
  onConfirm,
  "data-test": dataTest,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const formik = useForm({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    onSubmit: async (values) => {
      try {
        await AuthService.updateUserPassword(
          values.newPassword,
          values.oldPassword
        );
        setIsEditing(false);
        toast.success(t("user_profile.password_updated"));
      } catch (error) {
        if (error instanceof AxiosError) {
          const validationErrors = error.response?.data?.error?.details?.errors;
          if (validationErrors?.includes(COMMON_PASSWORD_ERROR)) {
            formik.setFieldTouched("newPassword", true, false);
            formik.setFieldError(
              "newPassword",
              "general.errors.invalid.common_password"
            );
            return;
          }
          if (error.response?.data?.error?.message === "Invalid old password") {
            toast.error(t("user_profile.invalid_old_password"));
            return;
          } else {
            toast.error(t("user_profile.password_update_error"));
          }
        } else {
          toast.error(t("user_profile.password_update_error"));
        }
      }
    },
    validationSchema: object({
      oldPassword: string().required(
        t("user_profile.current_password_required")
      ),
      newPassword: string()
        .required(t("user_profile.password_required"))
        .test(testPassword),
      confirmPassword: string()
        .required(t("user_profile.confirm_password_required"))
        .test(testPassword)
        .test(
          "passwords-match",
          t("user_profile.passwords_not_match"),
          (value): boolean => value === formik.values.newPassword
        ),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
  });

  const handleCancel = () => {
    setIsEditing(false);
    formik.resetForm();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
      }}
    >
      {isEditing ? (
        <Form formik={formik} data-test="updatePasswordInput.form">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: "8px",
              maxWidth: "900px",
              width: "100%",
              alignItems: { xs: "stretch", md: "flex-start" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: "8px",
                  alignItems: { xs: "stretch", md: "flex-start" },
                }}
              >
                <FlexColBox gap="8px" width={{ xs: "100%", md: "33.33%" }}>
                  <Box
                    sx={{
                      minHeight: "24px",
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography variant="textRegularBold">
                      {t("user_profile.current_password")}
                    </Typography>
                  </Box>
                  <Input
                    name="oldPassword"
                    type="password"
                    noMaxWidth
                    sx={{ width: "100%", height: "40px" }}
                    showPasswordToggle
                  />
                </FlexColBox>
                <FlexColBox gap="8px" width={{ xs: "100%", md: "33.33%" }}>
                  <Box
                    sx={{
                      minHeight: "24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <Typography variant="textRegularBold">
                      {t("user_profile.new_password")}
                    </Typography>
                    <Box sx={{ mt: "3px", mr: "10px" }}>
                      <PasswordStrengthIndicator
                        password={formik.values.newPassword}
                      />
                    </Box>
                  </Box>
                  <Input
                    name="newPassword"
                    type="password"
                    noMaxWidth
                    sx={{ width: "100%", height: "40px" }}
                    showPasswordToggle
                  />
                </FlexColBox>
                <FlexColBox gap="8px" width={{ xs: "100%", md: "33.33%" }}>
                  <Box
                    sx={{
                      minHeight: "24px",
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography variant="textRegularBold">
                      {t("user_profile.confirm_new_password")}
                    </Typography>
                  </Box>
                  <Input
                    name="confirmPassword"
                    type="password"
                    noMaxWidth
                    sx={{ width: "100%", height: "40px" }}
                    showPasswordToggle
                  />
                </FlexColBox>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                height: "40px",
                maxHeight: "40px",
                lineHeight: "0",
                mt: { xs: "16px", md: "33px" },
                justifyContent: { xs: "flex-start", md: "flex-start" },
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="primary"
                type="submit"
                sx={{
                  minHeight: "34px",
                  height: "40px",
                  maxHeight: "40px",
                  lineHeight: "0",
                }}
              >
                {t("general.save")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                sx={{
                  minHeight: "34px",
                  height: "40px",
                  maxHeight: "40px",
                  lineHeight: "0",
                }}
              >
                {t("general.cancel")}
              </Button>
            </Box>
          </Box>
        </Form>
      ) : (
        <FlexColBox gap="8px" fullWidth>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Typography variant="textRegularBold">
              {t("user_profile.password")}
            </Typography>
            <EditIcon
              onClick={() => setIsEditing(true)}
              style={{ cursor: "pointer" }}
            />
          </Box>
          <Typography variant="textRegular">********</Typography>
        </FlexColBox>
      )}
    </Box>
  );
};
